import React, { useRef, useState } from 'react';
import { useTranslations } from '../../../hooks/useTranslations';
import styles from './FileUpload.module.scss';

// Figmaからの正確なアップロードアイコン
const UploadIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <mask id="mask0_7292_65876" style={{maskType:"alpha"}} maskUnits="userSpaceOnUse" x="0" y="0" width="48" height="48">
      <rect width="48" height="48" fill="#D9D9D9"/>
    </mask>
    <g mask="url(#mask0_7292_65876)">
      <path d="M23 31.154V13.854L18.3385 18.5155L16.923 17.077L24 10L31.077 17.077L29.6615 18.5155L25 13.854V31.154H23ZM13.231 38C12.3103 38 11.5417 37.6917 10.925 37.075C10.3083 36.4583 10 35.6897 10 34.769V29.923H12V34.769C12 35.077 12.1282 35.3592 12.3845 35.6155C12.6408 35.8718 12.923 36 13.231 36H34.769C35.077 36 35.3592 35.8718 35.6155 35.6155C35.8718 35.3592 36 35.077 36 34.769V29.923H38V34.769C38 35.6897 37.6917 36.4583 37.075 37.075C36.4583 37.6917 35.6897 38 34.769 38H13.231Z" fill="#111111"/>
    </g>
  </svg>
);

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
  label?: string;
  accept?: string;
  maxSizeMB?: number;
  disabled?: boolean;
  error?: string;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFileSelect,
  selectedFile,
  label,
  accept = '.jpg,.jpeg,.png,.pdf,.ai',
  maxSizeMB = 20,
  disabled = false,
  error
}) => {
  const translations = useTranslations();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateFile = (file: File): { isValid: boolean; error?: string } => {
    const maxSize = maxSizeMB * 1024 * 1024;
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf', 'application/postscript'];

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: translations.fileUpload_fileSizeExceeded
          .replace('{maxSize}', maxSizeMB.toString())
          .replace('{currentSize}', (file.size / 1024 / 1024).toFixed(2))
      };
    }

    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: translations.fileUpload_invalidFileType
      };
    }

    return { isValid: true };
  };

  const handleFileSelect = (file: File) => {
    const validation = validateFile(file);
    
    if (!validation.isValid) {
      setValidationError(validation.error || translations.fileUpload_invalidFile);
      onFileSelect(null);
      return;
    }

    setValidationError(null);
    onFileSelect(file);
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);

    if (disabled) return;

    const file = event.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveFile = () => {
    setValidationError(null);
    onFileSelect(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const displayError = error || validationError;

  return (
    <div className={styles.container}>
      {label && <label className={styles.label}>{label}</label>}
      
      <div
        className={`
          ${styles.uploadArea}
          ${dragOver ? styles.dragOver : ''}
          ${disabled ? styles.disabled : ''}
          ${displayError ? styles.error : ''}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileInputChange}
          disabled={disabled}
          style={{ display: 'none' }}
        />

        {selectedFile ? (
          <div className={styles.selectedFile}>
            <div className={styles.fileInfo}>
              <span className={styles.fileName}>{selectedFile.name}</span>
              <span className={styles.fileSize}>
                ({(selectedFile.size / 1024 / 1024).toFixed(2)} {translations.fileUpload_mb})
              </span>
            </div>
            <button
              type="button"
              className={styles.removeButton}
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveFile();
              }}
              disabled={disabled}
            >
              {translations.fileUpload_removeFile}
            </button>
          </div>
        ) : (
          <div className={styles.placeholder}>
            <div className={styles.uploadIcon}>
              <UploadIcon />
            </div>
            <div className={styles.uploadContent}>
              <div className={styles.uploadText}>
                <div className={styles.mainText}>
                  {translations.fileUpload_uploadHere}
                </div>
                <div className={styles.subText}>
                  {translations.fileUpload_fileTypeInfo.replace('{maxSize}', maxSizeMB.toString())}
                </div>
              </div>
              <button
                type="button"
                className={styles.uploadButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick();
                }}
                disabled={disabled}
              >
                {label || translations.fileUpload_selectFile}
              </button>
            </div>
          </div>
        )}
      </div>

      {displayError && (
        <div className={styles.errorMessage}>
          {displayError}
        </div>
      )}
    </div>
  );
};

export default FileUpload; 